import { Injectable, EventEmitter } from '@angular/core';
import { BooksService } from '@app/modules/trading/modules/books/providers/books.service';
import { TradeBook } from '@app/modules/trading/modules/books/books.models';
import { AlgoSymbolPosition } from './algo-symbol-position.model';
import { QuoteStoreService } from '../quote-store';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { StrategiesService, RunningCompletedStrategy } from '../strategies';
import { SymbolSearchService } from '../symbol-search';
import { map } from 'rxjs/operators';
import { PortfolioService } from '../portfolio';
import { LTP_REFRESH_INTERVAL, Utils } from '@app/_helpers';
import { AffiliateService } from '@app/modules/affiliate/providers/affiliate.service';
import { AffiliateFundRow } from '@app/modules/affiliate/providers/affilaite.model';
import { MarketSocketService, SOCKET_MSG_TYPES, SYMBOL_SUBSCRIPTION_TYPE } from '../websocket';

@Injectable({
    providedIn: 'root'
})
export class AlgowisePositionDataService {
    portfolioAggregatePosition = new Map<string, AlgoSymbolPosition>()
    portfoiloSymbolPosition = new Map<string, Map<string, AlgoSymbolPosition>>();
    portfoiloStrategyLegPosition = new Map<string, Map<string, Map<string, AlgoSymbolPosition>>>();
    portfolioIdFundsObjMap = new Map<string, AffiliateFundRow>();
    symbolIdFundsObjMap = new Map<string, Set<AffiliateFundRow>>();
    firstFetchCalled = new EventEmitter();
    strategyPositionUpdate = new EventEmitter();
    hideShimmer: boolean = true;
    noLegitLtpMap = new Map<string, boolean>();


    constructor(
        private booksService: BooksService,
        private quoteService: QuoteStoreService,
        private portfolioService: PortfolioService,
        private symbolSearchService: SymbolSearchService,
        private marketSocketService: MarketSocketService,
        private quoteStore: QuoteStoreService
    ) {
        this.startRefreshInterval();
    }

    calculateAlgoWisePosition() {
        this.booksService.fetchOrderFirstFetch().subscribe();
        this.booksService.fetchPositionFirstFetch().subscribe();
        this.booksService.fetchTradeFirstFetch().subscribe(trades => {
            trades.forEach(trade => {
                this.updatePositionMap(trade);
            });
            this.subscribeSocket();
            this.strategyPositionUpdate.emit(trades);
            this.hideShimmer = false;
            this.firstFetchCalled.emit();

        })
    }

    subscribeSocket() {
        this.booksService.tradeBookUpdate.subscribe((newTrades: TradeBook[]) => {
            console.log("event caught");
            if (newTrades) {
                newTrades.forEach(trade => {
                    console.log("updating position map");
                    this.updatePositionMap(trade);
                });
            }
            this.strategyPositionUpdate.emit(newTrades);
        })
    }

    updatePositionMap(trade: TradeBook) {
        this.getAggregatedPosition(trade.market_id, trade.security_id, trade.product_type, trade.strategy_type, trade.broker).updateTrade(trade);
            // need to convert portfolio_id to string as fastApi server host is now sending this field in number
            // but we are expecting it to be string, if number is considered here then our mainted map will not work correctly.
            this.getSymbolPosition(trade.portfolio_id+"", trade.market_id, trade.security_id,
            trade.product_type, trade.strategy_type).updateTrade(trade);
        this.getPortfoiloSymbolPosition(trade.portfolio_id+"", trade.market_id, trade.security_id, trade.product_type,
            trade.strategy_type, trade.unique_algo_name, trade.leg_id).updateTrade(trade)
        let fundsObj = this.portfolioIdFundsObjMap.get(trade.portfolio_id);
        if (fundsObj) {
            let newSet = this.symbolIdFundsObjMap.get(Utils.getSymbolId(MarketIDMap[trade.market_id], trade.security_id).toString());
            newSet.add(fundsObj);
            this.symbolIdFundsObjMap.set(Utils.getSymbolId(MarketIDMap[trade.market_id], trade.security_id).toString(), newSet);

            if (this.marketSocketService.subscribedSymbols.get(this.quoteStore.getMapKey(MarketIDMap[trade.market_id], trade.security_id))[SOCKET_MSG_TYPES.LTP_Close_PRICE]) {
                this.subscribeFeed(trade.market_id, trade.security_id);
            }

        }


    }

    getPortfoiloSymbolPosition(portfolioID: string, market: string, securityID: number, product: any, strategyType: any, unique_algo_name: string, leg_id: string) {
        let key = Utils.getJoinedString(unique_algo_name, market, securityID, product, strategyType, leg_id);

        if (!this.getPortfoiloStrategyLegPosition(portfolioID, unique_algo_name).has(key)) {
            this.getPortfoiloStrategyLegPosition(portfolioID, unique_algo_name).set(key, new AlgoSymbolPosition(
                this.quoteService.getFeedObj(MarketIDMap[market], securityID),
                null,
                this.symbolSearchService.symbolInfo(`${MarketIDMap[market]}_${securityID}`)
            ));
        }
        return this.getPortfoiloStrategyLegPosition(portfolioID, unique_algo_name).get(key);
    }

    getSymbolPosition(portfolioID: string, market: string, securityID: number, product: any, strategyType: any) {

        let key = Utils.getJoinedString(market, securityID, product, strategyType);

        if (!this.getPortfolioPosition(portfolioID).has(key)) {
            this.getPortfolioPosition(portfolioID).set(key, new AlgoSymbolPosition(
                this.quoteService.getFeedObj(MarketIDMap[market], securityID),
                null,
                this.symbolSearchService.symbolInfo(`${MarketIDMap[market]}_${securityID}`)
            ));
        }
        return this.getPortfolioPosition(portfolioID).get(key);
    }

    getAggregatedPosition(market: string, securityID: number, product: any, strategyType: any, brokerName: string) {
        let key = Utils.getJoinedString(market, securityID, product, strategyType, brokerName);
        if (!this.portfolioAggregatePosition.has(key)) {
            this.portfolioAggregatePosition.set(key, new AlgoSymbolPosition(
                this.quoteService.getFeedObj(MarketIDMap[market], securityID),
                null,
                this.symbolSearchService.symbolInfo(`${MarketIDMap[market]}_${securityID}`)
            ))
        }
        return this.portfolioAggregatePosition.get(key);
    }



    getPortfoiloStrategyLegPosition(portfolioId: string, strategyId: string) {
        if (!this.portfoiloStrategyLegPosition.has(portfolioId)) {
            let assignMap = new Map<string, Map<string, AlgoSymbolPosition>>();
            this.portfoiloStrategyLegPosition.set(portfolioId, assignMap)
        }


        if (!this.portfoiloStrategyLegPosition.get(portfolioId).has(strategyId)) {
            const tempMap = new Map<string, AlgoSymbolPosition>();
            this.portfoiloStrategyLegPosition.get(portfolioId).set(strategyId, tempMap);
        }
        return this.portfoiloStrategyLegPosition.get(portfolioId).get(strategyId);
    }

    getPortfolioPosition(portfolioId: string) {

        if (!this.portfoiloSymbolPosition.has(portfolioId)) {
            let tempMap = new Map<string, AlgoSymbolPosition>();
            this.portfoiloSymbolPosition.set(portfolioId, tempMap)
            return this.portfoiloSymbolPosition.get(portfolioId);
        }
        return this.portfoiloSymbolPosition.get(portfolioId);
    }

    getPortfolioLevelPositions(portfolioId: string) {
        if (!this.portfoiloStrategyLegPosition.has(portfolioId)) {
            let assignMap = new Map<string, Map<string, AlgoSymbolPosition>>();
            this.portfoiloStrategyLegPosition.set(portfolioId, assignMap)
        }
        return this.portfoiloStrategyLegPosition.get(portfolioId);
    }

    reSubscribeLogic(market_id: string, security_id: number) {
        let mapQuote = this.quoteStore.quotes.get(this.quoteStore.getMapKey(MarketIDMap[market_id], security_id));
        if (mapQuote?.isLtp && mapQuote?.LTP) {
            this.noLegitLtpMap.delete(Utils.getJoinedString(market_id, security_id));
        } else {
            this.marketSocketService.sendUnsubscribeSymbol({ market_id: MarketIDMap[market_id], security_id: security_id }, SOCKET_MSG_TYPES.LTP_Close_PRICE);
            this.subscribeFeed(market_id, security_id);
        }
    }

    startRefreshInterval() {
        setInterval(() => {
            this.noLegitLtpMap.forEach((value, key) => {
                this.reSubscribeLogic(key.split('_')[0], parseInt(key.split('_')[1]));
            });

        }, LTP_REFRESH_INTERVAL);

    }

    subscribeFeed(marketId: string, securityId: number) {
        const key = Utils.getJoinedString(marketId, securityId);

        if (!this.noLegitLtpMap.has(key)) {
            this.noLegitLtpMap.set(key, true);
        }
        this.marketSocketService.sendSubscribeSymbol({ market_id: MarketIDMap[marketId], security_id: securityId }, SOCKET_MSG_TYPES.LTP_Close_PRICE, SYMBOL_SUBSCRIPTION_TYPE.FIRST_FETCH_SUBSCRIPTION)
    }
}
